import React from 'react';
import {
  Badge,
  Box,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { COUNT_CAP } from '@/shared/utils/countCap';
import {
  ChevronRight as ChevronRightIcon,
  ForumOutlined as ForumOutlinedIcon,
} from '@mui/icons-material';

export const DETAIL_CHAT_WIDTH = 380;

export function DetailChatToggleButton({
  enabled,
  collapsed,
  unreadCount = 0,
  expandLabel,
  collapseLabel,
  onToggle,
}: {
  enabled: boolean;
  collapsed: boolean;
  unreadCount?: number;
  expandLabel: string;
  collapseLabel: string;
  onToggle: () => void;
}) {
  if (!enabled) return null;
  return (
    <Tooltip title={collapsed ? expandLabel : collapseLabel}>
      <IconButton
        size="small"
        onClick={onToggle}
        aria-label={collapsed ? expandLabel : collapseLabel}
        aria-pressed={!collapsed}
        color={!collapsed ? 'primary' : 'default'}
        sx={{ flexShrink: 0, mx: 0.5 }}
      >
        <Badge
          color="primary"
          badgeContent={unreadCount}
          max={COUNT_CAP}
          invisible={unreadCount < 1}
        >
          <ForumOutlinedIcon />
        </Badge>
      </IconButton>
    </Tooltip>
  );
}

/**
 * Right-side chat column (desktop) + drawer (mobile). Parent owns collapse state.
 */
export function DetailChatRail({
  enabled,
  collapsed = true,
  onToggle,
  title,
  collapseLabel,
  children,
}: {
  enabled: boolean;
  collapsed?: boolean;
  onToggle?: () => void;
  title?: string;
  collapseLabel: string;
  children?: React.ReactNode;
}) {
  const theme = useTheme();
  const compactChat = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
  const chatOpen = enabled && !collapsed;
  const persistentChat = chatOpen && !compactChat;

  const chatHeader = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        px: 2,
        minHeight: 40,
        flexShrink: 0,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      {title ? (
        <Typography variant="subtitle2" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
          {title}
        </Typography>
      ) : (
        <Box sx={{ flex: 1 }} />
      )}
      {onToggle ? (
        <Tooltip title={collapseLabel}>
          <IconButton size="small" onClick={onToggle} aria-label={collapseLabel}>
            <ChevronRightIcon />
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  );

  const chatBody = (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {children}
    </Box>
  );

  if (!enabled) return null;

  return (
    <>
      <Box
        component="aside"
        sx={{
          width: persistentChat ? DETAIL_CHAT_WIDTH : 0,
          flexShrink: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          borderLeft: persistentChat ? 1 : 0,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          transition: (muiTheme) =>
            muiTheme.transitions.create('width', {
              easing: muiTheme.transitions.easing.sharp,
              duration: muiTheme.transitions.duration.enteringScreen,
            }),
          overflow: 'hidden',
        }}
      >
        {persistentChat ? (
          <>
            {chatHeader}
            {chatBody}
          </>
        ) : null}
      </Box>

      {compactChat ? (
        <Drawer
          anchor="right"
          open={chatOpen}
          onClose={onToggle}
          PaperProps={{
            sx: {
              width: DETAIL_CHAT_WIDTH,
              maxWidth: '100%',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          {chatHeader}
          {chatBody}
        </Drawer>
      ) : null}
    </>
  );
}
